import type { EnergyPackStatus } from '@goelectric/shared-types';
import {
  darkColors,
  neutralColors,
  semanticColors,
} from '@goelectric/ui-components';
import type { StatusIconKey } from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';
import {
  derivePackVisualStatus,
  PACK_OUT_OF_VALIDITY,
  PACK_VISUAL_STATUS_DESCRIPTIONS,
  PACK_VISUAL_STATUS_LABELS,
} from '../utils/format';

interface PackStatusMeta {
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Color e icono por estado: el estado nunca depende solo del color (WCAG 2.2 AA). */
const PACK_STATUS_META: Record<EnergyPackStatus, PackStatusMeta> = {
  ACTIVE: {
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
  EXPIRED: {
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'warning',
  },
  SOLD_OUT: {
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};

/** Color e icono del estado visual derivado por vigencia. */
const OUT_OF_VALIDITY_META: PackStatusMeta = {
  color: semanticColors.warning,
  colorDark: darkColors.warning,
  iconKey: 'warning',
};

/** Estado del pack de energía con color + icono + texto. */
export function PackStatusBadge({
  status,
  validTo,
  forceDark = false,
}: {
  status: EnergyPackStatus;
  validTo?: string | null;
  forceDark?: boolean;
}) {
  const visualStatus = derivePackVisualStatus(status, validTo);
  const meta =
    visualStatus === PACK_OUT_OF_VALIDITY
      ? OUT_OF_VALIDITY_META
      : PACK_STATUS_META[visualStatus];
  const label = PACK_VISUAL_STATUS_LABELS[visualStatus];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={label}
      title={`${label}: ${PACK_VISUAL_STATUS_DESCRIPTIONS[visualStatus]}`}
    />
  );
}
